import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Sparkles, Upload, Loader2 } from "lucide-react";
import { GarmentForm } from "./GarmentForm";

const toPayload = (g) => ({ ...g, cost_each: Number(g.cost_each) || 0, retail_price: Number(g.retail_price) || 0 });

export function GarmentAIImport({ open, onOpenChange, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [keep, setKeep] = useState([]);

  const extract = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const { data } = await api.post("/garments/ai-extract", fd);
      setItems(data.garments); setKeep(data.garments.map(() => true));
      toast.success(`Found ${data.garments.length} garment${data.garments.length === 1 ? "" : "s"} — review before saving`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not read the file"); }
    setBusy(false);
  };

  const save = async () => {
    const sel = items.filter((_, i) => keep[i]);
    try {
      for (const g of sel) await api.post("/garments", toPayload(g));
      toast.success(`Saved ${sel.length} garment${sel.length === 1 ? "" : "s"}`);
      setItems([]); onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="garment-ai-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles size={18} className="text-[#2495D3]" />Import garments with AI</DialogTitle></DialogHeader>
        <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl p-6 cursor-pointer hover:border-[#2495D3] transition-colors" data-testid="garment-ai-drop">
          {busy ? <Loader2 className="animate-spin text-[#2495D3]" /> : <Upload className="text-slate-400" />}
          <span className="text-sm text-slate-600">{busy ? "Reading catalogue… (about 20 s)" : "Upload the supplier PDF or screenshots"}</span>
          <span className="text-[11px] text-slate-400">The AI reads model, style #, sizes, colours and price</span>
          <input type="file" multiple accept=".pdf,image/*" className="hidden" disabled={busy} data-testid="garment-ai-file" onChange={(e) => { extract(e.target.files); e.target.value = ""; }} />
        </label>
        {items.map((g, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-4" data-testid={`garment-ai-item-${i}`}>
            <label className="flex items-center gap-2 mb-3 text-sm font-semibold"><Checkbox checked={keep[i]} onCheckedChange={(v) => setKeep(keep.map((k, idx) => (idx === i ? !!v : k)))} />Save this garment</label>
            <GarmentForm g={g} prefix={`ai-${i}`} onChange={(v) => setItems(items.map((x, idx) => (idx === i ? v : x)))} />
          </div>
        ))}
        {items.length > 0 && (
          <Button data-testid="garment-ai-save" onClick={save} disabled={!keep.some(Boolean)} className="bg-[#2495D3] hover:bg-[#1E7AA9] rounded-lg">Save {keep.filter(Boolean).length} to catalogue</Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
